import {api} from "../api.slice";
import {apiEndpoints} from "@/utils/api-endpoints";
import UserProfile, {UserDetails} from "@/types/user-profile";
import User from "@/types/user";
import {
    AssignRoleRequest,
    CreateAnonymousIdRequest,
    GetAllUsersRequest,
    GetAllUsersResponse,
    GetProfileResponse,
    GetUserProfilePictureNameResponse
} from "@/types/user";
import {SMSTemplates} from "@/types/SMS-templates";

const userApi = api.injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
        login: builder.mutation<User, { UserName: string; Password: string }>({
            query: (body) => ({
                ...apiEndpoints.User.Login,
                body,
            }),
            invalidatesTags: ['User', 'Profile'],
        }),

        sendVerificationCode: builder.mutation<void, { Mobile: string; SMSTemplate: SMSTemplates }>(
            {
                query: (body) => {
                    const endpoint =
                        body.SMSTemplate === SMSTemplates.REGISTER
                            ? apiEndpoints.User.SendRegisterVerificationCode
                            : apiEndpoints.User.SendForgotPasswordVerificationCode;

                    return {
                        ...endpoint,
                        body,
                    };
                },
            }
        ),

        resetPassword: builder.mutation<void, { UserName: string; Password: string }>({
            query: (body) => ({
                ...apiEndpoints.User.ResetPassword,
                body,
            }),
        }),

        checkVerificationCode: builder.mutation<void, { Mobile: string; VerificationCode: string }>({
            query: (body) => ({
                ...apiEndpoints.User.CheckVerificationCode,
                body,
            }),
        }),

        register: builder.mutation<User, UserDetails>({
            query: (body) => ({
                ...apiEndpoints.User.Register,
                body,
            }),
        }),

        refreshToken: builder.mutation<UserProfile, void>({
            query: () => ({
                ...apiEndpoints.User.RefreshToken,
            }),
        }),

        logout: builder.mutation<void, void>({
            query: () => ({
                ...apiEndpoints.User.RevokeToken,
                body: {},
            }),
            invalidatesTags: ['Cart', 'ChatsList', 'Messages', 'User', 'Profile'],
        }),

        getAllUsers: builder.query<GetAllUsersResponse, GetAllUsersRequest>({
            query: ({ searchText = '', page = 1, onlyOnline }) => {
                const params = new URLSearchParams();

                if (searchText) params.append('searchText', searchText);
                if (page) params.append('page', String(page));
                if (onlyOnline) params.append('onlyOnline', "true");

                return {
                    ...apiEndpoints.User.GetUsersAdmin(params.toString()),
                };
            },
            providesTags: ['Users'],
        }),
        getUserProfilePictureName: builder.query<GetUserProfilePictureNameResponse, void>({
            query: () => apiEndpoints.User.GetProfilePictureName.url,
            providesTags: ['ProfilePicture'],
        }),

        getProfile: builder.query<GetProfileResponse, { userId?: string } | void>({
            query: (args) => {
                const userId = args?.userId;

                return userId
                    ? { ...apiEndpoints.User.GetProfileUser(userId) }
                    : { ...apiEndpoints.User.GetProfile };
            },
            providesTags: (result, error, args) =>
                args?.userId
                    ? [{ type: 'Profile', id: args.userId }]
                    : ['Profile'],
        }),

        setProfilePicture: builder.mutation<string, { file: File | Blob; userId?: string }>({
            query: ({file, userId}) => {
                const body = new FormData();
                body.append('file', file);

                return userId
                    ? {
                        ...apiEndpoints.User.SetProfilePictureUser(userId),
                        body,
                    }
                    : {
                        ...apiEndpoints.User.SetProfilePicture,
                        body,
                    };
            },
            invalidatesTags: (result, error, {userId}) =>
                userId ? ['Users'] : ['Profile', 'User', 'ProfilePicture'],
        }),
        editProfile: builder.mutation<void, { data: Partial<UserDetails>; userId?: string }>({
            query: ({ data, userId }) => ({
                ...apiEndpoints.User.EditProfile(userId || ''),
                body: data,
            }),
            invalidatesTags: ['Profile'],
        }),
        editProfileUser: builder.mutation<void, { data: Partial<UserDetails>; userId?: string }>({
            query: ({ data, userId }) => ({
                ...apiEndpoints.User.EditProfileUser(userId || ''),
                body: data,
            }),
            invalidatesTags: (result, error, { userId }) =>
                userId
                    ? [{ type: 'Profile', id: userId }]
                    : ['Profile'],
        }),
        setIsBlocked: builder.mutation<void, { userId: string; }>({
            query: ({userId}) => ({
                ...apiEndpoints.User.BlockUser(userId || ''),
            }),
            invalidatesTags: ['Users'],
        }),
        setUnBlocked: builder.mutation<void, { userId: string; }>({
            query: ({userId}) => ({
                ...apiEndpoints.User.UnblockUser(userId || ''),
            }),
            invalidatesTags: ['Users'],
        }),

        getRoles: builder.query<string[], void>({
            query: () => apiEndpoints.User.GetRoles.url,
            providesTags: ['Roles'],
        }),

        assignRole: builder.mutation<void, AssignRoleRequest>({
            query: ({targetUserId, roleName}) => ({
                ...apiEndpoints.User.AssignRole(targetUserId),
                body: {roleName},
            }),
            invalidatesTags: ['Users'],
        }),

        revokeRole: builder.mutation<void, AssignRoleRequest>({
            query: ({targetUserId, roleName}) => ({
                ...apiEndpoints.User.TakeBackRole(targetUserId),
                body: {roleName},
            }),
            invalidatesTags: ['Users'],
        }),
        getUserPaymentDeadline: builder.query<{ data: number }, void>({
            query: () => apiEndpoints.Finance.GetSettleDeadline.url,
            providesTags: ['User'],
        }),

        createAnonymousUser: builder.mutation<string, CreateAnonymousIdRequest>({

            query: ({name, phoneNumber}) => ({
                ...apiEndpoints.User.AddAnonymousUser,
                body: {name, phoneNumber},
            }),
        }),
    }),
});

export const {
    useLoginMutation,
    useSendVerificationCodeMutation,
    useCheckVerificationCodeMutation,
    useRegisterMutation,
    useSetIsBlockedMutation,
    useSetUnBlockedMutation,
    useLogoutMutation,
    useGetProfileQuery,
    useSetProfilePictureMutation,
    useEditProfileMutation,
    useEditProfileUserMutation,
    useResetPasswordMutation,
    useGetAllUsersQuery,
    useGetUserProfilePictureNameQuery,
    useAssignRoleMutation,
    useRevokeRoleMutation,
    useGetRolesQuery,
    useGetUserPaymentDeadlineQuery,
    useCreateAnonymousUserMutation,
} = userApi;
